/* ==========================================================================
   ArgeMoonScene — embedded, container-scoped version of the blue moon.
   Difference from the standalone build: this fills its parent element
   (the .moon-hero__disc circle) instead of the whole viewport, and
   ArgeMoonScene.init(canvasId) can be called more than once for the same
   canvas — Shopify's theme editor re-renders section HTML on every
   settings change, so each instance tears down its previous WebGL
   context/render loop before starting a new one.
   ========================================================================== */

(function () {
  "use strict";

  // Registry so re-calling init() on the same canvas id cleans up first
  var instances = {};

  function disposeInstance(canvasId) {
    var inst = instances[canvasId];
    if (!inst) return;
    if (inst.state) inst.state.running = false;
    if (inst.resizeObserver) inst.resizeObserver.disconnect();
    if (inst.renderer) inst.renderer.dispose();
    delete instances[canvasId];
  }

  function init(canvasId, options) {
    if (typeof THREE === "undefined") {
      console.error("ArgeMoonScene.init: THREE is not loaded yet.");
      return;
    }

    options = options || {};
    var canvas = document.getElementById(canvasId);
    if (!canvas) return;

    disposeInstance(canvasId);

    var container = canvas.parentElement;
    var prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    var renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      antialias: true,
      alpha: true, // transparent so the section's own background shows through
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;

    var scene = new THREE.Scene();

    var camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0, 6.4);
    camera.lookAt(0, 0, 0);

    function resize() {
      var w = container.clientWidth || 1;
      var h = container.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();

    var resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    // ---- procedural textures (seeded, so it looks the same every load) ----
    var seed = 1337;
    function rand() {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    }
    function randRange(min, max) {
      return min + rand() * (max - min);
    }

    var TEX_SIZE = 1024; // smaller than the standalone build — this renders inside a page hero, not fullscreen

    function buildCraterField(count) {
      var craters = [];
      for (var i = 0; i < count; i++) {
        craters.push({
          x: rand() * TEX_SIZE,
          y: rand() * TEX_SIZE,
          r: randRange(2, 28) * (rand() < 0.03 ? 1.8 : 1),
          depth: randRange(0.3, 1),
        });
      }
      return craters;
    }

    var craterField = buildCraterField(600);

    function buildColorTexture() {
      var cvs = document.createElement("canvas");
      cvs.width = cvs.height = TEX_SIZE;
      var ctx = cvs.getContext("2d");

      var base = ctx.createLinearGradient(0, 0, TEX_SIZE, TEX_SIZE);
      base.addColorStop(0, "#0a1740");
      base.addColorStop(0.45, "#122a63");
      base.addColorStop(0.75, "#0d1f52");
      base.addColorStop(1, "#081334");
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, TEX_SIZE, TEX_SIZE);

      for (var i = 0; i < 20; i++) {
        var x = rand() * TEX_SIZE;
        var y = rand() * TEX_SIZE;
        var r = randRange(60, 170);
        var g = ctx.createRadialGradient(x, y, 0, x, y, r);
        var tone = rand() < 0.5 ? "26,58,120" : "18,38,90";
        g.addColorStop(0, "rgba(" + tone + ",0.55)");
        g.addColorStop(1, "rgba(" + tone + ",0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      var grain = ctx.getImageData(0, 0, TEX_SIZE, TEX_SIZE);
      var gd = grain.data;
      for (var p = 0; p < gd.length; p += 4) {
        var n = (rand() - 0.5) * 14;
        gd[p] = Math.min(255, Math.max(0, gd[p] + n));
        gd[p + 1] = Math.min(255, Math.max(0, gd[p + 1] + n));
        gd[p + 2] = Math.min(255, Math.max(0, gd[p + 2] + n * 1.1));
      }
      ctx.putImageData(grain, 0, 0);

      craterField.forEach(function (c) {
        var shadow = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r);
        shadow.addColorStop(0, "rgba(3,8,26," + 0.65 * c.depth + ")");
        shadow.addColorStop(0.7, "rgba(6,14,40," + 0.35 * c.depth + ")");
        shadow.addColorStop(1, "rgba(6,14,40,0)");
        ctx.fillStyle = shadow;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fill();

        var hlx = c.x - c.r * 0.28;
        var hly = c.y - c.r * 0.28;
        var hl = ctx.createRadialGradient(hlx, hly, 0, hlx, hly, c.r * 0.9);
        var icy = rand() < 0.3;
        hl.addColorStop(0, icy ? "rgba(190,225,255,0.16)" : "rgba(90,150,255,0.12)");
        hl.addColorStop(0.4, "rgba(70,120,220,0.12)");
        hl.addColorStop(1, "rgba(70,120,220,0)");
        ctx.fillStyle = hl;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fill();
      });

      var tex = new THREE.CanvasTexture(cvs);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      tex.encoding = THREE.sRGBEncoding;
      return tex;
    }

    function buildBumpTexture() {
      var cvs = document.createElement("canvas");
      cvs.width = cvs.height = TEX_SIZE;
      var ctx = cvs.getContext("2d");

      ctx.fillStyle = "#7f7f7f";
      ctx.fillRect(0, 0, TEX_SIZE, TEX_SIZE);

      craterField.forEach(function (c) {
        var cavity = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r);
        cavity.addColorStop(0, "rgba(20,20,20," + 0.85 * c.depth + ")");
        cavity.addColorStop(0.75, "rgba(60,60,60," + 0.4 * c.depth + ")");
        cavity.addColorStop(1, "rgba(60,60,60,0)");
        ctx.fillStyle = cavity;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fill();

        var rim = ctx.createRadialGradient(c.x, c.y, c.r * 0.75, c.x, c.y, c.r * 1.05);
        rim.addColorStop(0, "rgba(180,180,180,0)");
        rim.addColorStop(0.6, "rgba(200,200,200," + 0.22 * c.depth + ")");
        rim.addColorStop(1, "rgba(200,200,200,0)");
        ctx.fillStyle = rim;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r * 1.05, 0, Math.PI * 2);
        ctx.fill();
      });

      var tex = new THREE.CanvasTexture(cvs);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      return tex;
    }

    function buildRoughnessTexture() {
      var cvs = document.createElement("canvas");
      cvs.width = cvs.height = TEX_SIZE;
      var ctx = cvs.getContext("2d");
      ctx.fillStyle = "#c9c9c9";
      ctx.fillRect(0, 0, TEX_SIZE, TEX_SIZE);

      craterField.forEach(function (c) {
        var g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r);
        g.addColorStop(0, "rgba(150,150,150,0.5)");
        g.addColorStop(1, "rgba(150,150,150,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fill();
      });

      return new THREE.CanvasTexture(cvs);
    }

    var colorMap, bumpMap, roughnessMap, tintColor;

    if (options.imageUrl) {
      // Real photo mode: load the uploaded image, tint it blue.
      // MeshStandardMaterial multiplies its base "color" against "map",
      // so a grayscale/tan photo comes out blue without losing real detail.
      colorMap = new THREE.TextureLoader().load(options.imageUrl);
      colorMap.encoding = THREE.sRGBEncoding;
      colorMap.anisotropy = renderer.capabilities.getMaxAnisotropy();
      tintColor = new THREE.Color(0x5f8fe0);
      // No procedural bump/roughness in photo mode — a mismatched
      // synthetic crater bump would fight the real photo's own shading.
      bumpMap = null;
      roughnessMap = null;
    } else {
      colorMap = buildColorTexture();
      bumpMap = buildBumpTexture();
      roughnessMap = buildRoughnessTexture();
      tintColor = new THREE.Color(0xffffff);
    }

    var MOON_RADIUS = 1.7;
    var geometry = new THREE.SphereGeometry(MOON_RADIUS, 160, 160); // fewer segments than standalone — this is a page widget, not the hero showcase

    var material = new THREE.MeshStandardMaterial({
      map: colorMap,
      color: tintColor,
      bumpMap: bumpMap || undefined,
      bumpScale: bumpMap ? 0.045 : 0,
      roughnessMap: roughnessMap || undefined,
      roughness: 0.92,
      metalness: 0.06,
    });

    var moon = new THREE.Mesh(geometry, material);
    moon.position.set(0, 0, 0);
    moon.rotation.z = THREE.MathUtils.degToRad(8);
    moon.rotation.x = THREE.MathUtils.degToRad(-4);
    scene.add(moon);

    var glowGeometry = new THREE.SphereGeometry(MOON_RADIUS * 1.035, 96, 96);
    var glowMaterial = new THREE.ShaderMaterial({
      uniforms: { glowColor: { value: new THREE.Color(0x3a6bff) } },
      vertexShader:
        "varying vec3 vNormal; void main() { vNormal = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
      fragmentShader:
        "varying vec3 vNormal; uniform vec3 glowColor; void main() { float intensity = pow(0.62 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0); gl_FragColor = vec4(glowColor, clamp(intensity, 0.0, 1.0) * 0.55); }",
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
    });
    moon.add(new THREE.Mesh(glowGeometry, glowMaterial));

    scene.add(new THREE.AmbientLight(0x1a2340, 0.55));

    var keyLight = new THREE.DirectionalLight(0xdfe9ff, 2.1);
    keyLight.position.set(4.2, 2.4, 4.5);
    scene.add(keyLight);

    var rimLight = new THREE.PointLight(0x3f7bff, 1.4, 20, 2);
    rimLight.position.set(-4, -1.5, -3);
    scene.add(rimLight);

    var fillLight = new THREE.PointLight(0x1c2c66, 0.5, 20, 2);
    fillLight.position.set(-2, 3, 2);
    scene.add(fillLight);

    var rotationSpeed;
    if (prefersReducedMotion) {
      rotationSpeed = 0.0006;
    } else if (options.rotationSeconds) {
      // radians per frame at an assumed 60fps, matching the old CSS "seconds per full turn" setting
      rotationSpeed = (2 * Math.PI) / (options.rotationSeconds * 60);
    } else {
      rotationSpeed = 0.0022;
    }
    var wobbleAmplitude = THREE.MathUtils.degToRad(1.6);
    var wobbleSpeed = 0.00035;
    var baseTiltX = moon.rotation.x;
    var clock = new THREE.Clock();

    var state = { running: true };
    instances[canvasId] = {
      state: state,
      resizeObserver: resizeObserver,
      renderer: renderer,
    };

    function animate() {
      if (!state.running) return; // stopped by a later disposeInstance() call
      requestAnimationFrame(animate);
      var elapsedMs = clock.getElapsedTime() * 1000;
      moon.rotation.y += rotationSpeed;
      moon.rotation.x = baseTiltX + Math.sin(elapsedMs * wobbleSpeed) * wobbleAmplitude;
      renderer.render(scene, camera);
    }
    animate();
  }

  window.ArgeMoonScene = { init: init };
})();